import { Icon, IconProfile, IconQuestion } from "@flixbus/honeycomb-icons-react";
import {
  Avatar,
  Dropdown,
  Grid,
  GridCol,
  Header,
  HeaderBrand,
  HeaderBrandProps,
  HeaderBurgerMenu,
  HeaderNavigation,
  HeaderSubNavigation,
  HeaderUserWidget,
  HeaderWidgets,
  NavItem,
} from "@flixbus/honeycomb-react";
import * as React from "react";
import { Feature, hasUserPermission, useUserName } from "../../auth";
import { commonPathnames } from "../../helper/pathnames/pathnames";
import { useTheme } from "../../helper/themeToggler/themeToggler";
import HeaderStagingBackground from "../headerStagingBackground/HeaderStagingBackground";
import LANGUAGE_SWITCHER_PORTAL_ID from "../languageSwitcher/LanguageSwitcherPortalId";
import * as css from "./Navigation.scss";
import getDropdownHelpMenuItems from "./helpMenuNavigation/getDropdownHelpMenuItems/getDropdownHelpMenuItems";
import getHelpMenuItems from "./helpMenuNavigation/getHelpMenuItems/getHelpMenuItems";
import getNavigationItems from "./helpers/getNavigationItems";
import App from "./types";
import getWidgetNavigation from "./widgetNavigation/getWidgetNavigation";

const LOGO_FLIX_URL = "https://honeycomb-assets.hive.flixbus.com/honeycomb-logos-static/2.0.0/svg/flix-white.svg";
const LOGO_COOPERATION_URL =
  "https://honeycomb-assets.hive.flixbus.com/honeycomb-logos-static/2.0.0/svg/flixcoop-white.svg";

type Props = {
  app: App;
  widget?: JSX.Element;
  isUsedInternal: boolean;
  RouterLink?: HeaderBrandProps["RouterLink"];
};
const Navigation: React.FC<Props> = ({ app, widget, isUsedInternal, RouterLink }) => {
  const userName = useUserName();
  const navigation = getNavigationItems(app);

  const [isDarkTheme, toggleTheme] = useTheme();

  return (
    <>
      <Header
        brand={
          <HeaderBrand
            src={isUsedInternal ? LOGO_COOPERATION_URL : LOGO_FLIX_URL}
            alt={isUsedInternal ? "Flix Cooperation" : "Flix"}
            RouterLink={RouterLink}
            href={commonPathnames.userHome}
            height="20px"
            width="auto"
          />
        }
        navigation={
          hasUserPermission(Feature.USER_HAS_VIEW_ACCESS_TO_ALL_PAGES) && isUsedInternal ? (
            <HeaderNavigation data-id="desktop" aria-label="Main">
              {navigation}
            </HeaderNavigation>
          ) : (
            <></>
          )
        }
        burgerMenu={
          hasUserPermission(Feature.USER_HAS_VIEW_ACCESS_TO_ALL_PAGES) && isUsedInternal ? (
            <HeaderBurgerMenu openPanelLabel="Open main menu" closePanelLabel="Close main menu" panelId="burger-panel">
              <HeaderNavigation data-id="mobile" aria-label="Main">
                {[
                  ...navigation,
                  <NavItem
                    data-id="help-widget-mobile-menu"
                    key="helpMenu"
                    subMenu={
                      <HeaderSubNavigation id="help-mobile-menu">
                        {getHelpMenuItems(app, false).map((item) => (
                          <NavItem
                            key={item.key}
                            InlineIcon={item.InlineIcon}
                            href={item.href}
                            to={item.to}
                            target={item.target}
                            RouterLink={item.RouterLink}
                            shouldCloseDialog={item.shouldCloseDialog}
                          >
                            {item.text}
                          </NavItem>
                        ))}
                      </HeaderSubNavigation>
                    }
                    extraClasses={css.widgetMobileMenu}
                  >
                    <div className={css.subMenuHeaderMobile}>
                      <Icon InlineIcon={IconQuestion} size={5} />
                      <span>Help Center</span>
                    </div>
                  </NavItem>,
                  <NavItem
                    data-id="user-widget-mobile-menu"
                    key="userMenu"
                    subMenu={getWidgetNavigation(isDarkTheme, toggleTheme, true)}
                    extraClasses={css.widgetMobileMenu}
                  >
                    <div className={css.subMenuHeaderMobile}>
                      <Icon InlineIcon={IconProfile} size={5} />
                      <span>{userName}</span>
                    </div>
                  </NavItem>,
                ]}
              </HeaderNavigation>
            </HeaderBurgerMenu>
          ) : (
            <></>
          )
        }
        widgetSize="m"
        widgets={
          <HeaderWidgets>
            {isUsedInternal ? (
              <Grid>
                <GridCol>
                  <Dropdown id="help-menu-dropdown" links={getDropdownHelpMenuItems(app)}>
                    <Icon size={6} InlineIcon={IconQuestion} extraClasses={css.helpMenuIcon} />
                  </Dropdown>
                </GridCol>
                <GridCol>
                  <HeaderUserWidget
                    avatar={<Avatar size={6} />}
                    subMenu={getWidgetNavigation(isDarkTheme, toggleTheme)}
                  >
                    <div className={css.userName}>
                      {/* This is a prank I played on Julia. And its still funny */}
                      {userName === "Julia Jormakka" ? "Julia Eva Aili Maria Alvariina Jormakka" : userName}
                    </div>
                  </HeaderUserWidget>
                </GridCol>
              </Grid>
            ) : (
              widget
            )}
          </HeaderWidgets>
        }
        extraClasses={css.header}
        fixed
      />

      <HeaderStagingBackground />

      {/* portal into which the language switcher renders into */}
      <div id={LANGUAGE_SWITCHER_PORTAL_ID} />
    </>
  );
};

export default Navigation;
